import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import MaintenanceDocument from '../components/maintenance/MaintenanceDocument';
import { summarizeChecklistItem } from '../components/ChecklistRenderer';
import FieldReportToolbar from '../components/field-report/FieldReportToolbar';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MaintenancePreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  // Bug #5 fix (2026-09-06): Grease Form / Maintenance Request Form
  // approval used to happen in a separate "Approve Form" popup back on
  // the Maintenance page. Replaced with the same inline sign-in-the-
  // document pattern PreCon/CSE already use — approve/reject here act
  // directly on this same page, no popup. Lifted straight from
  // CSEPreview.js (identical approach, MaintenanceDocument already
  // supports it generically via the `approvalCapture` prop).
  const [showReject, setShowReject] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const sigCanvasRef = useRef(null);
  const [hasSignature, setHasSignature] = useState(false);
  const isDrawingRef = useRef(false);

  // Only real maintenance_forms records (Grease/Maintenance Request)
  // get the approval bar — asset transactions (Check-In/Check-Out/
  // Pre-Trip) approve via their own existing flow and are left alone.
  const canApprove = !!form?.can_approve && form?.source !== 'asset';
  const canReject = !!form?.can_reject && form?.source !== 'asset';

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      // First, try to fetch from maintenance forms
      const response = await axios.get(`${API}/maintenance/${id}`);
      const formDoc = response.data;

      // For template-backed forms (Grease / Maintenance Request /
      // Confined Space Evaluation) also fetch the resolved
      // checklist_template so MaintenanceDocument can render each
      // stored answer with its label + repeatable-row rows.
      if (formDoc?.checklist_template_title) {
        try {
          const tplRes = await axios.get(`${API}/checklist-templates`);
          const list = Array.isArray(tplRes.data) ? tplRes.data : [];
          const tpl = list.find(t => t.title === formDoc.checklist_template_title);
          if (tpl) formDoc.template = tpl;
        } catch (e) {
          console.error('Failed to load checklist template for preview:', e);
        }
      }

      setForm(formDoc);
    } catch (maintenanceErr) {
      // If maintenance form not found, try asset transaction
      if (maintenanceErr.response?.status === 404) {
        try {
          const assetResponse = await axios.get(`${API}/assets/transactions/${id}`);
          const transaction = assetResponse.data;
          
          // Extract mileage and condition from checklist items. The checklist
          // is stored using the template-backed shape (label/section/type/
          // value/selections) — `summarizeChecklistItem` also understands
          // the old pre-Feb-2026 flat `{item, checked}` shape for older records.
          const checklist = transaction.checklist || [];
          const summarized = checklist.map(summarizeChecklistItem);
          let mileageValue = null;
          let conditionValue = '';
          
          summarized.forEach(s => {
            const label = s.label.toLowerCase().trim();
            // Word-boundary match — avoids false positives like "Tank
            // Condition" matching a generic "vehicle condition" summary field.
            if (label.startsWith('mileage') && s.answerText) mileageValue = s.answerText;
            if (label.startsWith('condition') && s.answerText) conditionValue = s.answerText;
          });
          
          // Map checklist items to proper format. `checked` must stay false
          // for a failed (answered "no") item — MaintenanceDocument's
          // getChecklistStatus() only reads `checked`/`status`, so folding
          // `failed` into checked=true would print a green "Done" badge
          // for a flagged inspection item.
          const mappedChecklist = summarized.map(s => ({
            item: s.label,
            checked: s.answered && !s.failed,
            status: s.failed ? 'fail' : (s.answered ? 'pass' : 'pending'),
            notes: s.answerText || s.notes || '',
            value: s.answerText,
            photos: s.photos,
            photo: s.photo
          }));
          
          // Collect all photos from checklist items
          const allPhotos = summarized.reduce((photos, s) => {
            if (s.photos && s.photos.length > 0) {
              return [...photos, ...s.photos];
            }
            return photos;
          }, []);
          
          // Convert asset transaction to maintenance form format
          const convertedForm = {
            id: transaction.id,
            form_type: transaction.type === 'checkin' ? 'Equipment Check In' : transaction.type === 'pretrip' ? 'Pre-Trip Inspection' : 'Equipment Check Out',
            form_number: transaction.asset_number || transaction.id?.slice(0, 8),
            equipment_name: transaction.asset_name,
            equipment_id: transaction.asset_number,
            date: transaction.created_at || transaction.updated_at,
            performed_by: transaction.user_name,
            checklist: mappedChecklist,
            notes: transaction.notes || '',
            mileage: mileageValue,
            condition: conditionValue || transaction.condition || '',
            // Fix 2 (2026-08-30): legacy asset transactions predating the
            // approval workflow have no approval_status field, which made
            // getStatusLabel() fall back to "Draft" for a fully-completed
            // historical record. Missing approval_status = nothing was
            // ever pending, so treat it as approved.
            status: transaction.approval_status || 'approved',
            submitter_name: transaction.user_name,
            submitted_at: transaction.created_at,
            submitter_signature_url: transaction.signature_url,
            reviewer_name: transaction.reviewed_by_name,
            reviewed_at: transaction.reviewed_at,
            reviewer_signature_url: transaction.manager_signature,
            created_at: transaction.created_at,
            rejection_reason: transaction.rejection_reason,
            rejected_at: transaction.rejected_at,
            photos: allPhotos,
            source: 'asset'
          };
          setForm(convertedForm);
        } catch (assetErr) {
          console.error('Failed to fetch asset transaction:', assetErr);
          toast.error('Form not found');
        }
      } else {
        console.error('Failed to fetch maintenance form:', maintenanceErr);
        toast.error('Failed to load form');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      reload();
    }
  }, [id, reload]);

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate(-1);
  };

  // ---- signature pad plumbing (same approach as CSEPreview.js) --------
  const getCanvasPoint = (e) => {
    const canvas = sigCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - rect.left) * scaleX, y: (src.clientY - rect.top) * scaleY };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    const { x, y } = getCanvasPoint(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    isDrawingRef.current = true;
  };

  const draw = (e) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasPoint(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasSignature) setHasSignature(true);
  };

  const stopDrawing = () => { isDrawingRef.current = false; };

  const clearSignature = () => {
    const canvas = sigCanvasRef.current;
    if (canvas) canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const submitApprove = async () => {
    if (!hasSignature) {
      toast.error('Please sign in the Manager Approval box above before approving');
      return;
    }
    setApproving(true);
    try {
      const dataUrl = sigCanvasRef.current.toDataURL('image/png');
      await axios.post(`${API}/maintenance/${id}/approve`, { signature: dataUrl });
      toast.success('Form approved');
      clearSignature();
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };

  const submitReject = async () => {
    setRejecting(true);
    try {
      await axios.post(`${API}/maintenance/${id}/reject`, { reason: rejectReason || null });
      toast.success('Form rejected');
      setShowReject(false);
      setRejectReason('');
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject');
    } finally {
      setRejecting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-600">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading form...</span>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600 mb-4">Form not found</p>
          <button 
            onClick={handleBack}
            className="text-amber-600 hover:text-amber-700 font-medium"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      <FieldReportToolbar 
        title="Equipment Form Preview"
        onPrint={handlePrint}
        onBack={handleBack}
      />
      <div className={`py-10 px-4 flex justify-center print:p-0 print:block ${canApprove ? 'pb-32 sm:pb-24' : ''}`} id="print-wrapper">
        <MaintenanceDocument
          form={form}
          printRef={printRef}
          approvalCapture={canApprove ? {
            canApprove,
            sigCanvasRef,
            onStartDrawing: startDrawing,
            onDraw: draw,
            onStopDrawing: stopDrawing,
            onClear: clearSignature,
          } : null}
        />
      </div>

      {/* Approve / Reject action bar — same inline pattern as
          CSEPreview.js: acts on the signature drawn directly in the
          document above, no popup. */}
      {(canApprove || canReject) && (
        <div
          className="no-print fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-lg"
          data-testid="maintenance-preview-approval-bar"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div className="max-w-4xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm text-slate-600">
              {canApprove
                ? (hasSignature
                  ? 'Signature captured — approve or reject below.'
                  : 'Sign in the Manager Approval box above, then approve.')
                : 'This form is awaiting review.'}
            </span>
            <div className="flex gap-2">
              {canReject && (
                <Button
                  variant="outline"
                  onClick={() => setShowReject(true)}
                  className="border-rose-300 text-rose-700 hover:bg-rose-50"
                  data-testid="maintenance-preview-reject-btn"
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Reject
                </Button>
              )}
              {canApprove && (
                <Button
                  onClick={submitApprove}
                  disabled={!hasSignature || approving}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white"
                  data-testid="maintenance-preview-approve-btn"
                >
                  {approving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                  Approve
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject modal — reason optional (backend accepts null) */}
      <Dialog open={showReject} onOpenChange={setShowReject}>
        <DialogContent className="max-w-md" data-testid="maintenance-preview-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject Form</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Provide a reason (optional). The submitter will see this note when
              they open the form.
            </p>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Why is this form being rejected?"
              rows={4}
              data-testid="maintenance-preview-reject-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReject(false)}>Cancel</Button>
            <Button
              onClick={submitReject}
              disabled={rejecting}
              className="bg-rose-600 hover:bg-rose-500 text-white"
              data-testid="maintenance-preview-reject-confirm-btn"
            >
              {rejecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
              Confirm Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
